import React, { useRef, useState, useMemo, useEffect } from 'react';
import { Card, Form, Space, Spin, Typography, Button, Col, message, Radio, InputNumber, Input } from 'antd';
import AriaDatePicker from 'components/AriaDatePicker';
import { useDispatch } from 'react-redux';
import useProviderEditDisabled from 'hooks/useProviderEditDisabled';
import { ADD_AIR_FORCE_HCC_STEPS } from 'constants';
import providerService from 'services/providers.service';
import { actions as centerActions } from 'features/add-center';
import { validateDates } from 'utils';
import { PROVIDER_FILE_TYPES } from 'services/providers.service';
import UploadFile from 'components/UploadFile/UploadFile';
import { useAsync } from 'react-async-hook';
import { useSelector } from 'react-redux';
import { selectors } from 'features/auth';
import { DatePickerFormItemLabel, FormItemLabel } from 'components/Form';
import ExternalLink from 'components/ExternalLink/ExternalLink';

const { Title, Text } = Typography;

const AirForceHCCCredentials = ({ id, setStep, isFormSubmitting, loading, applicationSubmitted }) => {
  const [credentialsFiles, setCredentialsFiles] = useState(null);
  const [form] = Form.useForm();
  const accredited = Form.useWatch('accredited', form);
  const dispatch = useDispatch();
  const isSubmitDisabled = useProviderEditDisabled();
  const organization = useSelector(selectors.selectOrganization);
  const [fetchedAccreditations, setFetchedAccreditations] = useState([]);

  // Keeps track of whether `next` is pressed or `save`
  const shouldGoToNextStep = useRef(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const fetchedAccreditations = await providerService.getAccreditations(id);
        setFetchedAccreditations(fetchedAccreditations);

        if (fetchedAccreditations?.length) {
          form.setFieldsValue({
            accreditationNumber: fetchedAccreditations[0].accreditationNumber,
            startDate: fetchedAccreditations[0].startDate,
            endDate: fetchedAccreditations[0].endDate,
            id: fetchedAccreditations[0].id ? fetchedAccreditations[0].id : null,
          });
        }

        form.setFieldsValue({
          accredited: fetchedAccreditations == null ? undefined : fetchedAccreditations.length > 0,
        });
      } catch (error) {
        console.error('Error fetching accreditations', error);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useAsync(async () => {
    if (organization?.id !== undefined) {
      try {
        const _applicationFiles = await providerService.getApplicationFiles(id);
        setCredentialsFiles(_applicationFiles);
      } catch (err) {
        console.error('Error fetching organization files', err);
      }
    }
  }, [organization]);

  const accreditationFiles = useMemo(
    () => credentialsFiles?.filter((file) => file?.fileType === PROVIDER_FILE_TYPES.NATIONAL_ACCREDITATION.code),
    [credentialsFiles],
  );

  const diplomaDocs = useMemo(
    () => credentialsFiles?.filter((file) => file?.fileType === PROVIDER_FILE_TYPES.DIPLOMA.code),
    [credentialsFiles],
  );

  const handleDeleteAccreditation = async (accreditationId) => {
    try {
      await providerService.deleteAccreditation(id, accreditationId);
    } catch (error) {
      console.error('Error deleting accreditation', error);
    }
  };

  return (
    <div id="credentials" data-testid="credentials">
      <Spin spinning={isFormSubmitting || loading}>
        <Form
          layout="vertical"
          form={form}
          disabled={applicationSubmitted}
          scrollToFirstError
          onFinish={async (values) => {
            try {
              dispatch(centerActions.setIsFormSubmitting(true));
              const nafccType = {
                id: 2,
                title: 'NAFCC',
                description: 'National Association of Family Child Care',
                doesNotExpire: false,
                ageRangeRequired: false,
                sequenceNumber: 14,
                hasSelfStudy: true,
                homeOnly: true,
              };

              // Add/Update/Delete Accreditations
              if (!accredited) {
                await Promise.all(
                  fetchedAccreditations.map((accreditation) => handleDeleteAccreditation(accreditation.id)),
                );
                form.setFieldsValue({
                  accreditationNumber: null,
                  startDate: null,
                  endDate: null,
                  id: null,
                });
              } else {
                await providerService.addUpdateAccreditation(id, { ...values, application: { id }, type: nafccType });
              }

              if (shouldGoToNextStep.current) {
                setStep(ADD_AIR_FORCE_HCC_STEPS.HOURS_OF_OPERATION);
              }
            } catch (err) {
              newrelic.noticeError(err);
              message.error('Unable to save application details.');
            } finally {
              dispatch(centerActions.setIsFormSubmitting(false));
            }
          }}
        >
          <Title level={1}>Credentials</Title>
          <Card
            title={
              <Title level={2} className="mt-4">
                Accreditation
              </Title>
            }
            className="mb-4"
          >
            <div className="hidden">
              <Form.Item name="id">
                <InputNumber />
              </Form.Item>
            </div>
            <Form.Item
              name="accredited"
              label={
                <FormItemLabel
                  title="Do you have a NAFCC accreditation?"
                  subTitle="Providing your NAFCC accreditation will make you eligible for higher pay."
                />
              }
            >
              <Radio.Group data-testid="accredited">
                <Space direction="vertical">
                  <Radio value={true} data-testid="yesRadio">
                    Yes
                  </Radio>
                  <Radio value={false} data-testid="noRadio">
                    No
                  </Radio>
                </Space>
              </Radio.Group>
            </Form.Item>
            {accredited && (
              <>
                <Col xs={18} sm={16} md={12}>
                  <Form.Item
                    name="accreditationNumber"
                    label="nafcc accreditation number"
                    rules={[{ required: true, message: 'Accreditation number is required' }]}
                    data-testid="accreditationNumber"
                  >
                    <Input />
                  </Form.Item>
                </Col>

                <Col xs={18} sm={16} md={6}>
                  <Form.Item
                    name="startDate"
                    label={<DatePickerFormItemLabel title="Start date" />}
                    rules={[
                      {
                        required: true,
                        message: 'Start date is required',
                      },
                      {
                        validator: validateDates(
                          form.getFieldValue,
                          'startDate',
                          'endDate',
                          'Start date must be before expiration date',
                        ),
                      },
                    ]}
                    data-testid="startDate"
                  >
                    <AriaDatePicker
                      placeholder="Start Date"
                      format="MM/DD/YYYY"
                      style={{ borderRadius: '2px !important' }}
                      disabled={applicationSubmitted}
                    />
                  </Form.Item>
                  <Form.Item
                    name="endDate"
                    label={<DatePickerFormItemLabel title="Expiration date" />}
                    rules={[
                      { required: true, message: 'Expiration date is required' },
                      {
                        validator: validateDates(
                          form.getFieldValue,
                          'startDate',
                          'endDate',
                          'Expiration date must be after start date',
                        ),
                      },
                    ]}
                    data-testid="endDate"
                  >
                    <AriaDatePicker
                      placeholder="Expiration Date"
                      format="MM/DD/YYYY"
                      style={{ borderRadius: '2px' }}
                      disabled={applicationSubmitted}
                    />
                  </Form.Item>
                </Col>

                <Form.Item
                  label={<FormItemLabel title="upload nafcc accreditation certificate" showAsterisk />}
                  name="accreditationCert"
                  rules={[
                    () => ({
                      validator() {
                        if (accreditationFiles.length === 0) {
                          return Promise.reject('File is required');
                        }

                        return Promise.resolve();
                      },
                    }),
                  ]}
                  data-testid="accreditationCert"
                  className="max-w-96"
                >
                  <UploadFile
                    fileCode={PROVIDER_FILE_TYPES.NATIONAL_ACCREDITATION.code}
                    files={credentialsFiles}
                    setFiles={setCredentialsFiles}
                    applicationId={id}
                    readOnly={applicationSubmitted}
                    data-testid={'accreditationUpload'}
                  />
                </Form.Item>
              </>
            )}
          </Card>
          <Card
            title={
              <Title level={2} className="mt-4">
                Education
              </Title>
            }
            className="mb-4"
          >
            <Form.Item
              label={
                <FormItemLabel
                  title="upload high school diploma, ged, or high school self-certification statement"
                  subTitle={
                    <div>
                      <Text type="secondary">
                        If you’re unable to obtain your high school diploma or transcripts download and complete
                        the&nbsp;
                        <ExternalLink
                          text="high school self-certification statement document. "
                          href="/docs/self_certification_statement.pdf"
                        />
                      </Text>
                      <Text className="block mt-4" type="secondary">
                        Acceptable formats: pdf, png, jpg
                      </Text>
                    </div>
                  }
                  showAsterisk
                />
              }
              name="diploma"
              rules={[
                () => ({
                  validator() {
                    if (diplomaDocs.length === 0) {
                      return Promise.reject('File is required');
                    }

                    return Promise.resolve();
                  },
                }),
              ]}
              data-testid="diplomaUpload"
            >
              <UploadFile
                fileCode={PROVIDER_FILE_TYPES.DIPLOMA.code}
                files={credentialsFiles}
                setFiles={setCredentialsFiles}
                applicationId={id}
                readOnly={applicationSubmitted}
                data-testid={'educationUpload'}
                ariaLabel={
                  "Upload your high school diploma, GED, or high school self-certification statement. If you're unable to obtain your high school diploma or transcripts download and complete the high school self-certification statement document."
                }
              />
            </Form.Item>
            <Form.Item
              label={
                <FormItemLabel
                  title="upload continuing education documents"
                  subTitle={
                    <>
                      You may also provide one of the following documents to meet educational requirements:
                      <ul style={{ listStyleType: 'disc' }}>
                        <li>Child Development Associate (CDA) Credential</li>
                        <li>Early Childhood Education/Child Development Degree</li>
                        <li>Transcript listing 24+ college credits in these areas</li>
                      </ul>
                      Acceptable formats: pdf, png, jpg
                    </>
                  }
                />
              }
              name="continuationDocs"
              className="mb-0 max-w-96"
            >
              <UploadFile
                fileCode={PROVIDER_FILE_TYPES.EDUCATION.code}
                files={credentialsFiles}
                setFiles={setCredentialsFiles}
                applicationId={id}
                readOnly={applicationSubmitted}
                data-testid={'continuationUpload'}
                ariaLabel={
                  'Upload continuing education documents. You may also provide one of the following documents to meet educational requirements: Child Development Associate (CDA) Credential, Early Childhood Education/Child Development Degree, Transcript listing 24+ college credits in these areas. Acceptable formats: pdf, png, jpg'
                }
              />
            </Form.Item>
          </Card>

          <div className="flex space-x-2">
            <Button
              onClick={() => setStep(ADD_AIR_FORCE_HCC_STEPS.PROGRAM_INFO)}
              style={{ borderRadius: '0px' }}
              aria-label="back button"
              disabled={false}
              data-testod="backButton"
            >
              Back
            </Button>

            <div className="flex-grow" />

            {!applicationSubmitted && (
              <Button
                style={{ borderRadius: '0px' }}
                htmlType="submit"
                onClick={() => {
                  shouldGoToNextStep.current = false;
                }}
                disabled={isFormSubmitting || isSubmitDisabled}
                loading={isFormSubmitting}
                aria-label="save button"
              >
                Save
              </Button>
            )}

            <Button
              style={{ borderRadius: '0px' }}
              type="primary"
              role="button"
              htmlType={!applicationSubmitted ? 'submit' : undefined}
              onClick={() => {
                if (applicationSubmitted) {
                  setStep(ADD_AIR_FORCE_HCC_STEPS.HOURS_OF_OPERATION);
                  return;
                }
                shouldGoToNextStep.current = true;
              }}
              disabled={isFormSubmitting || isSubmitDisabled}
              loading={isFormSubmitting}
              data-testid="credentials-form-submit"
              aria-label="next button"
            >
              Next
            </Button>
          </div>
        </Form>
      </Spin>
    </div>
  );
};

export default AirForceHCCCredentials;
